import { DataTable } from "@/components/datatable/components/DataTable";
import { tableSearchSchema, safeDecompress } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import PageHeader from "@/components/common/PageHeader";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { auditLogsKeys } from "@/query-keys";
import type { AuditLog } from "@/schemas/auditLogSchema";
import { services } from "@/services";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import useAuditLogsQuery from "./audit-logs/_hooks/useAuditLogsQuery";
import useAuditLogColumns from "./audit-logs/_hooks/useColumns";

export const Route = createFileRoute("/_private/audit-logs")({
  beforeLoad: ({ context }) => {
    if (!context.auth.user?.isRoot) {
      throw redirect({ to: "/stats" });
    }
  },
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: auditLogsKeys.lists(deps.q),
      queryFn: () => services.getAuditLogs(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorBoundary error={error} />,
});

function RouteComponent() {
  const search = Route.useSearch();
  const { t } = useTranslation();
  const loaderData = Route.useLoaderData();
  const { data: queryData } = useAuditLogsQuery(search.q, loaderData);

  const columns = useAuditLogColumns();

  return (
    <div className="flex-1 flex flex-col gap-6">
      <PageHeader title={t("auditLog.title")} subtitle={t("auditLog.subtitle")} />

      <DataTable<AuditLog>
        searchPlaceholder={t("auditLog.searchPlaceholder")}
        data={queryData?.items}
        nextCursor={queryData?.nextCursor}
        hasMore={queryData?.hasMore}
        columns={columns}
        queryKey={auditLogsKeys.lists(search.q)}
        navigateFrom="/audit-logs"
        searchFrom="/_private/audit-logs"
        showActions={false}
        searchable={true}
      />
    </div>
  );
}
